'use client'

import { Text } from '@mantine/core'
import { type DataTableColumn } from 'mantine-datatable'
import { useParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { useSsrContext } from '@/contexts/ssr'
import { isUserInRestrictedRegion } from '@/i18n/country'
import type { RouteLocaleType } from '@/i18n/locales'

import CoinColumn from '../../CoinsTable/Table/columns/Coin'
import type { TableRecord } from '../../CoinsTable/Table/columns/configs'
import MarketCapColumn from '../../CoinsTable/Table/columns/MarketCap'
import PriceColumn from '../../CoinsTable/Table/columns/Price'
import PriceChangeColumn from '../../CoinsTable/Table/columns/PriceChange'
import PriceChartColumn from '../../CoinsTable/Table/columns/PriceChart'
import VolumeColumn from '../../CoinsTable/Table/columns/Volume'

function TradeCell({ restrictedAreas }: { restrictedAreas: string[] }) {
  const t = useTranslations()
  const { locale } = useParams<{ locale: RouteLocaleType }>()
  const { countryCode } = useSsrContext()
  const isRestricted = isUserInRestrictedRegion(restrictedAreas, countryCode, locale)

  return (
    <Text
      variant="subtitle-emphasis"
      c="var(--content-active)"
      style={{
        wordBreak: 'keep-all',
        visibility: isRestricted ? 'hidden' : 'visible',
      }}
    >
      {t('coinTable.row.trade')}
    </Text>
  )
}

export function useHomeCoinsColumns(restrictedAreasMap?: Map<string, string[]>) {
  const t = useTranslations('HomePageV2.coinsTable')
  const tHeaders = useTranslations('coinTable.headers')

  return useMemo<DataTableColumn<TableRecord>[]>(
    () => [
      {
        accessor: 'coin',
        title: tHeaders('coin'),
        ellipsis: true,
        render: coin => <CoinColumn coin={coin} />,
      },
      {
        accessor: 'price',
        title: tHeaders('price'),
        textAlign: 'right',
        width: '18%',
        render: coin => <PriceColumn coin={coin} />,
      },
      {
        accessor: '24hChange',
        title: t('24hChange'),
        textAlign: 'right',
        width: '17%',
        render: coin => <PriceChangeColumn coin={coin} />,
      },
      {
        accessor: '24hChart',
        title: t('24HChart'),
        textAlign: 'right',
        width: '17%',
        render: coin => <PriceChartColumn coinId={coin.token_id} />,
      },
      {
        accessor: 'marketCap',
        title: t('marketCap'),
        textAlign: 'right',
        width: '17%',
        render: coin => <MarketCapColumn coin={coin} />,
      },
      {
        accessor: 'volume',
        title: t('24hVolume'),
        textAlign: 'right',
        width: '17%',
        render: coin => <VolumeColumn coin={coin} />,
      },
      {
        accessor: 'trade',
        title: '',
        textAlign: 'right',
        render: coin => <TradeCell restrictedAreas={restrictedAreasMap?.get(coin.slug) ?? []} />,
      },
    ],
    [t, tHeaders, restrictedAreasMap],
  )
}
